import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import Header from '../components/Header';
import Footer from '../components/Footer';
import PodcastEpisodeCard from '../components/PodcastEpisodeCard';

const Media = () => {
  const [podcastEpisodes, setPodcastEpisodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch podcast episodes from API
  useEffect(() => {
    const fetchEpisodes = async () => {
      try {
        setLoading(true);
        const response = await fetch('/api/episodes');
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        setPodcastEpisodes(data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching podcast episodes:', error);
        setError('Failed to load podcast episodes. Please try again later.');
        setLoading(false);
      }
    };

    fetchEpisodes();
  }, []); // Empty dependency array means this runs once on component mount

  return (
    <>
      <Helmet>
        <title>Media Resources | Redemption Psychiatry</title>
        <meta name="description" content="Explore podcasts, videos, and other media resources from Redemption Psychiatry, including RPsych Radio episodes featuring expert discussions on mental health topics." />
      </Helmet>

      <div className="wrap media-page">
        <Header />
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div id="content" role="main" className="home">
            {/* Interior Header */}
            <div id="interior-header-container" className="container-fluid">
              <div className="container">
                <h1>Media Resources</h1>
              </div>
            </div>

            {/* Media Introduction */}
            <div className="container py-5">
              <div className="row mb-5">
                <div className="col-12">
                  <h2 className="text-center mb-4">RPsych Radio & Video Content</h2>
                  <p className="lead text-center">
                    Explore our growing collection of podcasts, videos, and expert discussions on mental health topics, 
                    innovative treatments, and wellness strategies from our team of specialists.
                  </p>
                </div>
              </div>

              {/* Podcast Episode Section */}
              <div className="podcast-episodes">
                <motion.h3 
                  className="section-title mb-4"
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                >
                  Latest Episodes
                </motion.h3>
                
                {loading && (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-3">Loading episodes...</p>
                  </div>
                )}
                
                {error && (
                  <div className="alert alert-danger" role="alert">
                    {error}
                  </div>
                )}
                
                <div className="row mb-5">
                  {/* Map through available episodes */}
                  {podcastEpisodes.map((episode, index) => (
                    <motion.div 
                      key={episode.id}
                      className="col-lg-6 mb-4"
                      initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ 
                        type: "spring", 
                        stiffness: 100, 
                        damping: 15, 
                        delay: 0.2 + (index * 0.1)
                      }}
                    >
                      <PodcastEpisodeCard episode={episode} index={index} />
                    </motion.div>
                  ))}
                  
                  {/* "More episodes coming soon" placeholder - show only if there's exactly ONE episode */}
                  {podcastEpisodes.length === 1 && (
                    <motion.div 
                      className="col-lg-6 mb-4"
                      initial={{ opacity: 0, x: 30 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ 
                        type: "spring", 
                        stiffness: 100, 
                        damping: 15, 
                        delay: 0.3
                      }}
                    >
                      <motion.div 
                        className="card h-100 shadow-sm bg-light d-flex align-items-center justify-content-center"
                        whileHover={{ scale: 1.02, boxShadow: "0 10px 20px rgba(45, 79, 145, 0.1)" }}
                        transition={{ type: "spring", stiffness: 400, damping: 25 }}
                      >
                        <div className="card-body text-center p-5">
                          <motion.h4 
                            className="text-muted mb-3 placeholder-title"
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.4 }}
                          >
                            More Episodes Coming Soon
                          </motion.h4>
                          <motion.p 
                            className="card-text text-muted episode-card-description"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.5 }}
                          >
                            We're working on new episodes of RPsych Radio. Subscribe to our channels to be notified when 
                            new episodes are released.
                          </motion.p>
                          <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.6 }}
                            className="mt-4"
                          >
                            <a 
                              href="https://www.youtube.com/@RedemptionPsychiatry" 
                              className="youtube-watch-btn"
                              target="_blank" 
                              rel="noopener noreferrer"
                            >
                              <svg 
                                xmlns="http://www.w3.org/2000/svg" 
                                width="16" 
                                height="16" 
                                fill="currentColor" 
                                className="bi bi-youtube me-1" 
                                viewBox="0 0 16 16"
                              >
                                <path d="M8.051 1.999h.089c.822.003 4.987.033 6.11.335a2.01 2.01 0 0 1 1.415 1.42c.101.38.172.883.22 1.402l.01.104.022.26.008.104c.065.914.073 1.77.074 1.957v.075c-.001.194-.01 1.108-.082 2.06l-.008.105-.009.104c-.05.572-.124 1.14-.235 1.558a2.007 2.007 0 0 1-1.415 1.42c-1.16.312-5.569.334-6.18.335h-.142c-.309 0-1.587-.006-2.927-.052l-.17-.006-.087-.004-.171-.007-.171-.007c-1.11-.049-2.167-.128-2.654-.26a2.007 2.007 0 0 1-1.415-1.419c-.111-.417-.185-.986-.235-1.558L.09 9.82l-.008-.104A31.4 31.4 0 0 1 0 7.68v-.123c.002-.215.01-.958.064-1.778l.007-.103.003-.052.008-.104.022-.26.01-.104c.048-.519.119-1.023.22-1.402a2.007 2.007 0 0 1 1.415-1.42c.487-.13 1.544-.21 2.654-.26l.17-.007.172-.006.086-.003.171-.007A99.788 99.788 0 0 1 7.858 2h.193zM6.4 5.209v4.818l4.157-2.408L6.4 5.209z"/>
                              </svg>
                              Subscribe on YouTube
                            </a>
                          </motion.div>
                        </div>
                      </motion.div>
                    </motion.div>
                  )}
                  
                  {/* Placeholder for future episodes - show only if there are no episodes */}
                  {podcastEpisodes.length === 0 && (
                    <motion.div 
                      className="col-lg-6 mx-auto"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ 
                        type: "spring", 
                        stiffness: 100, 
                        damping: 15, 
                        delay: 0.4
                      }}
                    >
                      <motion.div 
                        className="card h-100 shadow-sm bg-light d-flex align-items-center justify-content-center"
                        whileHover={{ scale: 1.02, boxShadow: "0 10px 20px rgba(0, 0, 0, 0.1)" }}
                        transition={{ type: "spring", stiffness: 400, damping: 25 }}
                      >
                        <div className="card-body text-center p-5">
                          <motion.h4 
                            className="text-muted mb-3 placeholder-title"
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.6 }}
                          >
                            No Episodes Available
                          </motion.h4>
                          <motion.p 
                            className="card-text text-muted episode-card-description"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.7 }}
                          >
                            Please check back soon for new episodes of RPsych Radio, featuring discussions on mental health 
                            topics, innovative treatments, and wellness strategies.
                          </motion.p>
                        </div>
                      </motion.div>
                    </motion.div>
                  )}
                </div>
              </div>

              {/* Subscribe Section */}
              <div className="subscribe-section bg-light p-4 rounded mb-5">
                <div className="row align-items-center">
                  <div className="col-12">
                    <h3 className="mb-3">Stay Updated with Our Latest Content</h3>
                    <p>
                      Subscribe to our channels to be notified when new episodes are released. 
                      Follow us on social media for updates on upcoming topics and guest appearances.
                    </p>
                  </div>
                </div>
                <div className="row mt-4 justify-content-center">
                  <motion.div 
                    className="col-md-4 mb-3"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    whileHover={{ scale: 1.03 }}
                  >
                    <a 
                      href="https://www.youtube.com/@RedemptionPsychiatry" 
                      className="btn btn-lg w-100 d-flex align-items-center justify-content-center social-btn"
                      target="_blank" 
                      rel="noopener noreferrer"
                    >
                      <svg 
                        xmlns="http://www.w3.org/2000/svg" 
                        width="24" 
                        height="24" 
                        fill="currentColor" 
                        className="bi bi-youtube me-2" 
                        viewBox="0 0 16 16"
                      >
                        <path d="M8.051 1.999h.089c.822.003 4.987.033 6.11.335a2.01 2.01 0 0 1 1.415 1.42c.101.38.172.883.22 1.402l.01.104.022.26.008.104c.065.914.073 1.77.074 1.957v.075c-.001.194-.01 1.108-.082 2.06l-.008.105-.009.104c-.05.572-.124 1.14-.235 1.558a2.007 2.007 0 0 1-1.415 1.42c-1.16.312-5.569.334-6.18.335h-.142c-.309 0-1.587-.006-2.927-.052l-.17-.006-.087-.004-.171-.007-.171-.007c-1.11-.049-2.167-.128-2.654-.26a2.007 2.007 0 0 1-1.415-1.419c-.111-.417-.185-.986-.235-1.558L.09 9.82l-.008-.104A31.4 31.4 0 0 1 0 7.68v-.123c.002-.215.01-.958.064-1.778l.007-.103.003-.052.008-.104.022-.26.01-.104c.048-.519.119-1.023.22-1.402a2.007 2.007 0 0 1 1.415-1.42c.487-.13 1.544-.21 2.654-.26l.17-.007.172-.006.086-.003.171-.007A99.788 99.788 0 0 1 7.858 2h.193zM6.4 5.209v4.818l4.157-2.408L6.4 5.209z"/>
                      </svg>
                      Subscribe on YouTube
                    </a>
                  </motion.div>
                  <motion.div 
                    className="col-md-4 mb-3"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.1 }}
                    whileHover={{ scale: 1.03 }}
                  >
                    <a 
                      href="https://www.facebook.com/RedemptionPsychiatry" 
                      className="btn btn-lg w-100 d-flex align-items-center justify-content-center social-btn"
                      target="_blank" 
                      rel="noopener noreferrer"
                    >
                      <svg 
                        xmlns="http://www.w3.org/2000/svg" 
                        width="24" 
                        height="24" 
                        fill="currentColor" 
                        className="bi bi-facebook me-2" 
                        viewBox="0 0 16 16"
                      >
                        <path d="M16 8.049c0-4.446-3.582-8.05-8-8.05C3.58 0-.002 3.603-.002 8.05c0 4.017 2.926 7.347 6.75 7.951v-5.625h-2.03V8.05H6.75V6.275c0-2.017 1.195-3.131 3.022-3.131.876 0 1.791.157 1.791.157v1.98h-1.009c-.993 0-1.303.621-1.303 1.258v1.51h2.218l-.354 2.326H9.25V16c3.824-.604 6.75-3.934 6.75-7.951z"/>
                      </svg>
                      Follow on Facebook
                    </a>
                  </motion.div>
                  <motion.div 
                    className="col-md-4 mb-3"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                    whileHover={{ scale: 1.03 }}
                  >
                    <a 
                      href="https://www.instagram.com/redemptionpsychiatry" 
                      className="btn btn-lg w-100 d-flex align-items-center justify-content-center social-btn"
                      target="_blank" 
                      rel="noopener noreferrer"
                    >
                      <svg 
                        xmlns="http://www.w3.org/2000/svg" 
                        width="24" 
                        height="24" 
                        fill="currentColor" 
                        className="bi bi-instagram me-2" 
                        viewBox="0 0 16 16"
                      >
                        <path d="M8 0C5.829 0 5.556.01 4.703.048 3.85.088 3.269.222 2.76.42a3.917 3.917 0 0 0-1.417.923A3.927 3.927 0 0 0 .42 2.76C.222 3.268.087 3.85.048 4.7.01 5.555 0 5.827 0 8.001c0 2.172.01 2.444.048 3.297.04.852.174 1.433.372 1.942.205.526.478.972.923 1.417.444.445.89.719 1.416.923.51.198 1.09.333 1.942.372C5.555 15.99 5.827 16 8 16s2.444-.01 3.298-.048c.851-.04 1.434-.174 1.943-.372a3.916 3.916 0 0 0 1.416-.923c.445-.445.718-.891.923-1.417.197-.509.332-1.09.372-1.942C15.99 10.445 16 10.173 16 8s-.01-2.445-.048-3.299c-.04-.851-.175-1.433-.372-1.941a3.926 3.926 0 0 0-.923-1.417A3.911 3.911 0 0 0 13.24.42c-.51-.198-1.092-.333-1.943-.372C10.443.01 10.172 0 7.998 0h.003zm-.717 1.442h.718c2.136 0 2.389.007 3.232.046.78.035 1.204.166 1.486.275.373.145.64.319.92.599.28.28.453.546.598.92.11.281.24.705.275 1.485.039.843.047 1.096.047 3.231s-.008 2.389-.047 3.232c-.035.78-.166 1.203-.275 1.485a2.47 2.47 0 0 1-.599.919c-.28.28-.546.453-.92.598-.28.11-.704.24-1.485.276-.843.038-1.096.047-3.232.047s-2.39-.009-3.233-.047c-.78-.036-1.203-.166-1.485-.276a2.478 2.478 0 0 1-.92-.598 2.48 2.48 0 0 1-.6-.92c-.109-.281-.24-.705-.275-1.485-.038-.843-.046-1.096-.046-3.233 0-2.136.008-2.388.046-3.231.036-.78.166-1.204.276-1.486.145-.373.319-.64.599-.92.28-.28.546-.453.92-.598.282-.11.705-.24 1.485-.276.738-.034 1.024-.044 2.515-.045v.002zm4.988 1.328a.96.96 0 1 0 0 1.92.96.96 0 0 0 0-1.92zm-4.27 1.122a4.109 4.109 0 1 0 0 8.217 4.109 4.109 0 0 0 0-8.217zm0 1.441a2.667 2.667 0 1 1 0 5.334 2.667 2.667 0 0 1 0-5.334z"/>
                      </svg>
                      Follow on Instagram
                    </a>
                  </motion.div>
                </div>
              </div>
              
              {/* Topic Suggestions */}
              <div className="topic-suggestions mb-5">
                <h3 className="section-title mb-4">Suggest a Topic</h3>
                <p>
                  Is there a mental health topic you'd like us to cover in a future episode? 
                  We welcome your suggestions and questions.
                </p>
                <div className="text-center mt-4">
                  <a href="/contact" className="button blue-border">Contact Us with Your Ideas</a>
                </div>
              </div>
            </div>
          </div>
          <div className="push"></div>
        </motion.div>
        <Footer />
      </div>
    </>
  );
};

export default Media;